import React, {
  useEffect,
  useContext,
  useState,
  useMemo,
  useCallback,
} from "react";
import { Image } from "uikit-react";
import { AppContexts } from "../../../providers";
import { getAuthUrl } from "../../../services/email.service.js";

import { host } from "../../../http.js";

const EmailIntegration = () => {
  const { settings, set, active } = useContext(AppContexts.SettingsContext);

  const { emailAccounts, getEmails, disconnectEmail } = useContext(
    AppContexts.EmailContext,
  );

  const [confirmEmailDisconnect, setConfirmEmailDisconnect] = useState(null);
  const [pendingEmailDisconnect, setPendingEmailDisconnect] = useState(null);
  const [showEmailAddForm, setShowEmailAddForm] = useState(false);
  const [emailAccountName, setEmailAccountName] = useState("");
  const [emailAccountEmail, setEmailAccountEmail] = useState("");
  const [emailErrors, setEmailErrors] = useState([]);

  useEffect(() => {
    getEmails();
  }, []);

  const handleEmailConnect = async (id) => {
    let errs = [];

    if (!emailAccountName.trim()) {
      errs.push("name");
    }

    if (!emailAccountEmail.trim()) {
      errs.push("email");
    }

    if (errs.length > 0) {
      setEmailErrors(errs);
      return;
    }

    window.location.assign(
      `${host}/v2/email/auth/url/?account_name=${emailAccountName.trim()}&account_email=${emailAccountEmail.trim()}`,
    );
  };

  const handleEmailDisconnect = async (id) => {
    setPendingEmailDisconnect(id);

    await disconnectEmail(id);

    setConfirmEmailDisconnect(null);
    setPendingEmailDisconnect(null);
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const activeAccounts = useMemo(() => {
    return emailAccounts.filter((acc) => acc.status === "connected");
  }, [emailAccounts]);

  return (
    <li
      className={`setting-tab-content ${
        active === "integrations" ? "uk-active" : ""
      }`}
    >
      <div className="integrations-detail-content">
        <div className="integration-connection-page">
          <div className="uk-flex uk-flex-between uk-flex-top uk-margin-bottom">
            <div>
              <h2>Email Connections</h2>
              <p className="uk-text-muted uk-margin-small-top">
                Sync one or more email addresses with RueBaRue to send/receive
                emails from that account in RueBaRue. Only email threads
                including one or more emails attached to either a guest
                reservation, contact, or CRM connection will be imported into
                RueBaRue, all other email traffic will be ignored
              </p>
            </div>
            <img
              src="/images/integration-logos/email-logo.svg"
              alt="Email"
              className="integration-title-logo"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          </div>

          {/* Summary Bar */}
          {emailAccounts.length > 0 && (
            <div className="integration-summary-bar uk-margin-bottom">
              <div className="integration-summary-stat">
                <span uk-icon="icon: users; ratio: 0.8"></span>
                <div>
                  <div className="integration-summary-label">
                    Email Accounts
                  </div>
                  <div className="integration-summary-value">
                    {
                      (emailAccounts || []).filter(
                        (a) => a.status === "connected",
                      ).length
                    }
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Account Cards */}
          <div className="integration-accounts-list">
            {activeAccounts.map((email, idx) => (
              <div
                key={email.id}
                className={`integration-account-card connected`}
              >
                <div className="integration-account-card-inner">
                  <div className="uk-flex uk-flex-middle" style={{ gap: 14 }}>
                    <div>
                      <div
                        className="uk-flex uk-flex-middle"
                        style={{ gap: 8 }}
                      >
                        <span className="integration-account-name">
                          {email.name}
                        </span>
                        <span className={`integration-badge connected`}>
                          Connected
                        </span>
                      </div>
                      <div className="integration-account-meta uk-margin-xsmall-top">
                        {email.email && (
                          <span className="integration-account-email">
                            {email.email}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="integration-account-actions">
                    {confirmEmailDisconnect === email.id ? (
                      <div className="integration-confirm-disconnect">
                        <span className="integration-confirm-text">
                          Remove?
                        </span>
                        <button
                          className="uk-button hms-btn hms-red-btn btn-no-margin"
                          type="button"
                          onClick={() => handleEmailDisconnect(email.ota_id)}
                        >
                          Yes
                        </button>
                        <button
                          className="uk-button hms-btn hms-white-btn btn-no-margin"
                          type="button"
                          onClick={() => setConfirmEmailDisconnect(null)}
                        >
                          No
                        </button>
                      </div>
                    ) : (
                      <button
                        className="uk-button hms-btn hms-red-outline-btn btn-no-margin"
                        type="button"
                        onClick={() => setConfirmEmailDisconnect(email.id)}
                      >
                        <span
                          uk-icon="icon: trash; ratio: 0.7"
                          className="uk-margin-small-right"
                        ></span>
                        Disconnect
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Add Account Form / Button */}
          {showEmailAddForm ? (
            <div className="integration-add-form uk-margin-bottom">
              <div className="hms-headings uk-margin-bottom">
                Connect a new email account
              </div>
              <div className="uk-grid uk-grid-small uk-margin-bottom" uk-grid>
                <div className="uk-width-1-2">
                  <label className="uk-form-label">Email Account Name *</label>
                  <div className="uk-form-controls">
                    <input
                      className="uk-input"
                      type="text"
                      value={emailAccountName}
                      onChange={(e) => setEmailAccountName(e.target.value)}
                      placeholder="e.g. Primary Email Account"
                    />
                  </div>
                </div>
                <div className="uk-width-1-2">
                  <label className="uk-form-label">Email Address *</label>
                  <div className="uk-form-controls">
                    <input
                      className="uk-input"
                      type="email"
                      value={emailAccountEmail}
                      onChange={(e) => setEmailAccountEmail(e.target.value)}
                      placeholder="host@example.com"
                    />
                  </div>
                </div>
              </div>
              <div className="uk-flex" style={{ gap: 10 }}>
                <button
                  className="uk-button hms-btn hms-gray-btn btn-no-margin"
                  type="button"
                  onClick={handleEmailConnect}
                >
                  Connect Email Account
                </button>
                <button
                  className="uk-button hms-btn hms-white-btn btn-no-margin"
                  type="button"
                  onClick={() => {
                    setShowEmailAddForm(false);
                    setEmailAccountName("");
                    setEmailAccountEmail("");
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              className="integration-add-account-btn uk-margin-bottom"
              type="button"
              onClick={() => setShowEmailAddForm(true)}
            >
              <span uk-icon="icon: plus; ratio: 0.8"></span>
              {emailAccounts.length === 0
                ? "Connect Email Account"
                : "Add Another Account"}
            </button>
          )}

          {/* Empty State */}
          {emailAccounts.length === 0 && !showEmailAddForm && (
            <div className="integration-empty-state uk-margin-bottom">
              <img
                src="/images/integration-logos/email-logo.svg"
                alt=""
                className="integration-empty-logo"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
              <div className="hms-headings">No email accounts connected</div>
              <p className="uk-text-muted uk-margin-small-top">
                Connect your email accounts to start syncing RueBaRue related
                emails
              </p>
            </div>
          )}
        </div>
      </div>
    </li>
  );
};

export default EmailIntegration;
